import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { fabric } from "fabric";
import config from "../config";
import {
  Plus,
  Play,
  Square,
  Type,
  Image,
  Circle as CircleIcon,
  Square as Rectangle,
  Trash2,
  Copy,
  Cloud,
  CloudOff,
  ChevronLeft,
  ChevronRight,
  Bold,
  Italic,
  Underline,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Save,
  ExternalLink,
  Link,
  Check,
} from "lucide-react";
import { v4 as uuidv4 } from "uuid";
import io from "socket.io-client";
import "./Editor.css";

const Editor = () => {
  const { presentationId } = useParams();
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const fabricRef = useRef(null);
  const socketRef = useRef(null);
  const fileInputRef = useRef(null);
  const pollingIntervalRef = useRef(null);
  const isLoadingSlide = useRef(false);
  const isSaving = useRef(false);
  const autoSaveIntervalRef = useRef(null);
  const autoSaveCountdownRef = useRef(null);
  const currentSlideIndexRef = useRef(0); // Referência para o índice atual

  const [slides, setSlides] = useState([{
    id: uuidv4(),
    content: JSON.stringify({ objects: [], version: "5.3.0" }),
    thumbnail: ''
  }]);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamId, setStreamId] = useState("");
  const [selectedObject, setSelectedObject] = useState(null);
  const [syncStatus, setSyncStatus] = useState("");
  const [presentationName, setPresentationName] = useState("Sem título");
  const [dbPresentationId, setDbPresentationId] = useState(null);
  const [isGeneratingStreamId, setIsGeneratingStreamId] = useState(false);
  const [copied, setCopied] = useState(false);
  const [usePolling, setUsePolling] = useState(false);
  const [streamError, setStreamError] = useState("");
  const [autoSaveCountdown, setAutoSaveCountdown] = useState(10);
  const [textStyles, setTextStyles] = useState({
    bold: false,
    italic: false,
    underline: false,
    align: "left",
  });

  // Manter currentSlideIndexRef sincronizado
  useEffect(() => {
    currentSlideIndexRef.current = currentSlideIndex;
  }, [currentSlideIndex]);

  // Função para salvar APENAS o slide atual - CORRIGIDA DEFINITIVAMENTE
  const saveCurrentSlideContent = useCallback(() => {
    if (!fabricRef.current || isLoadingSlide.current || isSaving.current) return;

    isSaving.current = true;
    
    // Capturar o conteúdo do canvas
    const canvasContent = fabricRef.current.toJSON();
    const content = JSON.stringify(canvasContent);
    const thumbnail = fabricRef.current.toDataURL({
      format: "png",
      quality: 0.3,
      multiplier: 0.2,
    });
    
    // Usar o índice da referência que está sempre atualizado
    const indexToUpdate = currentSlideIndexRef.current;
    
    console.log(`💾 Salvando slide ${indexToUpdate}, canvas tem ${canvasContent.objects?.length || 0} objetos`);
    
    // Atualizar o estado dos slides de forma imutável
    setSlides(prevSlides => {
      // Criar uma nova array com spread operator
      const newSlides = [...prevSlides];
      
      // Verificar se o índice é válido
      if (indexToUpdate >= 0 && indexToUpdate < newSlides.length) {
        // Criar um novo objeto slide APENAS para o índice específico
        newSlides[indexToUpdate] = {
          id: prevSlides[indexToUpdate].id, // Manter o mesmo ID
          content: content,
          thumbnail: thumbnail
        };
        
        console.log(`✅ Slide ${indexToUpdate} atualizado com ${canvasContent.objects?.length || 0} objetos`);
      }
      
      return newSlides;
    });

    // Se estiver em streaming, enviar atualização
    if (isStreaming && streamId) {
      if (socketRef.current?.connected && !usePolling) {
        socketRef.current.emit("update-slide", {
          presentationId: streamId,
          slideIndex: indexToUpdate,
          slideData: canvasContent,
          slideContent: content
        });
      } else if (usePolling) {
        updateSlidePolling();
      }
    }
    
    isSaving.current = false;
  }, [isStreaming, streamId, usePolling]);

  // Auto-save
  useEffect(() => {
    // Limpar intervalos anteriores
    if (autoSaveIntervalRef.current) {
      clearInterval(autoSaveIntervalRef.current);
    }
    if (autoSaveCountdownRef.current) {
      clearInterval(autoSaveCountdownRef.current);
    }

    // Só ativar auto-save se tiver ID da apresentação
    if (!dbPresentationId) return;

    // Resetar countdown
    setAutoSaveCountdown(10);

    // Interval para countdown
    autoSaveCountdownRef.current = setInterval(() => {
      setAutoSaveCountdown(prev => {
        if (prev <= 1) {
          return 10;
        }
        return prev - 1;
      });
    }, 1000);

    // Interval para auto-save
    autoSaveIntervalRef.current = setInterval(() => {
      syncToCloud();
    }, 10000);

    return () => {
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current);
      }
      if (autoSaveCountdownRef.current) {
        clearInterval(autoSaveCountdownRef.current);
      }
    };
  }, [dbPresentationId]);

  // Inicializar Canvas
  useEffect(() => {
    if (!canvasRef.current) return;

    console.log("🎨 Inicializando canvas...");
    
    const canvas = new fabric.Canvas(canvasRef.current, {
      width: 1024,
      height: 576,
      backgroundColor: "#ffffff",
    });

    fabricRef.current = canvas;

    // Event listeners
    canvas.on("selection:created", (e) => {
      setSelectedObject(e.selected[0]);
      updateTextStyles(e.selected[0]);
    });

    canvas.on("selection:updated", (e) => {
      setSelectedObject(e.selected[0]);
      updateTextStyles(e.selected[0]);
    });

    canvas.on("selection:cleared", () => {
      setSelectedObject(null);
      setTextStyles({
        bold: false,
        italic: false,
        underline: false,
        align: "left",
      });
    });

    // Salvar mudanças automaticamente com debounce
    let saveTimeout;
    const handleCanvasChange = () => {
      if (!isLoadingSlide.current && !isSaving.current) {
        clearTimeout(saveTimeout);
        saveTimeout = setTimeout(() => {
          saveCurrentSlideContent();
        }, 500);
      }
    };

    canvas.on("object:modified", handleCanvasChange);
    canvas.on("object:added", () => {
      if (!isLoadingSlide.current) {
        handleCanvasChange();
      }
    });
    canvas.on("object:removed", handleCanvasChange);
    canvas.on("text:changed", handleCanvasChange);

    // Carregar apresentação se tiver ID
    if (presentationId && presentationId !== "new") {
      loadPresentation(presentationId);
    }

    return () => {
      clearTimeout(saveTimeout);
      canvas.dispose();
    };
  }, []);

  // Inicializar Socket
  useEffect(() => {
    console.log("🔌 Conectando Socket.io...");
    
    socketRef.current = io(config.serverUrl, {
      transports: ["polling", "websocket"],
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 1000,
    });

    socketRef.current.on("connect", () => {
      console.log("✅ Socket conectado:", socketRef.current.id);
      setUsePolling(false);
      setStreamError("");
    });

    socketRef.current.on("presentation-started", (data) => {
      console.log("✅ Apresentação iniciada:", data.presentationId);
      setStreamId(data.presentationId);
      setIsGeneratingStreamId(false);
      setStreamError("");
      setIsStreaming(true);
    });

    socketRef.current.on("error", (error) => {
      console.error("❌ Erro do socket:", error);
      setStreamError(error.message || "Erro ao iniciar apresentação");
      setIsGeneratingStreamId(false);
    });

    socketRef.current.on("disconnect", () => {
      console.log("❌ Socket desconectado");
      if (isStreaming) {
        console.warn("⚠️ Socket desconectado durante streaming, mudando para polling...");
        startPollingMode();
      }
    });

    socketRef.current.on("connect_error", (error) => {
      console.error("❌ Erro de conexão:", error.message);
    });

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [isStreaming]);

  // Atualizar estilos de texto
  const updateTextStyles = (obj) => {
    if (obj && obj.type === "i-text") {
      setTextStyles({
        bold: obj.fontWeight === "bold",
        italic: obj.fontStyle === "italic",
        underline: obj.underline || false,
        align: obj.textAlign || "left",
      });
    }
  };

  // Função para usar polling ao invés de Socket.io
  const startPollingMode = async () => {
    console.log("🔄 Usando modo polling HTTP...");
    setUsePolling(true);
    
    try {
      // Salvar slide atual antes de enviar
      saveCurrentSlideContent();
      
      // Aguardar um pouco para garantir que o estado foi atualizado
      await new Promise(resolve => setTimeout(resolve, 200));
      
      // Pegar os slides do estado atual
      setSlides(currentSlides => {
        const slidesToSend = currentSlides.map(slide => ({
          id: slide.id || uuidv4(),
          content: slide.content || JSON.stringify({ objects: [], version: "5.3.0" }),
          thumbnail: slide.thumbnail || ''
        }));

        // Fazer a requisição com os slides atuais
        fetch(`${config.serverUrl}/api/presentation/update`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            presentationId: "pde",
            action: "start",
            slides: slidesToSend,
            currentSlide: currentSlideIndexRef.current,
            presenterId: socketRef.current?.id || "http-presenter"
          })
        }).then(response => {
          if (response.ok) {
            setIsStreaming(true);
            setStreamId("pde");
            setIsGeneratingStreamId(false);
            setStreamError("");
            console.log("✅ Streaming iniciado via polling");
          } else {
            throw new Error("Falha ao iniciar streaming via polling");
          }
        }).catch(error => {
          console.error("❌ Erro ao iniciar polling:", error);
          setStreamError("Erro ao iniciar apresentação");
          setIsGeneratingStreamId(false);
        });

        return currentSlides;
      });
    } catch (error) {
      console.error("❌ Erro ao iniciar polling:", error);
      setStreamError("Erro ao iniciar apresentação");
      setIsGeneratingStreamId(false);
    }
  };

  // Atualizar slide via polling
  const updateSlidePolling = async () => {
    if (!usePolling || !isStreaming) return;
    
    try {
      await fetch(`${config.serverUrl}/api/presentation/update`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          presentationId: "pde",
          action: "update",
          slides: slides,
          currentSlide: currentSlideIndexRef.current
        })
      });
    } catch (error) {
      console.error("Erro ao atualizar via polling:", error);
    }
  };

  // Carregar apresentação
  const loadPresentation = async (id) => {
    try {
      const response = await fetch(`${config.serverUrl}/api/presentations/${id}`);
      const data = await response.json();
      
      setDbPresentationId(data._id);
      setPresentationName(data.name || "Sem título");
      
      if (data.slides && data.slides.length > 0) {
        // Garantir que os slides tenham conteúdo válido
        const validSlides = data.slides.map(slide => ({
          ...slide,
          content: slide.content || JSON.stringify({ objects: [], version: "5.3.0" })
        }));
        
        setSlides(validSlides);
        setCurrentSlideIndex(0);
        currentSlideIndexRef.current = 0;
        
        // Carregar primeiro slide
        isLoadingSlide.current = true;
        if (validSlides[0].content) {
          const slideData = JSON.parse(validSlides[0].content);
          fabricRef.current.loadFromJSON(slideData, () => {
            fabricRef.current.renderAll();
            isLoadingSlide.current = false;
          });
        } else {
          isLoadingSlide.current = false;
        }
      }
    } catch (error) {
      console.error("❌ Erro ao carregar:", error);
    }
  };

  // Salvar na nuvem
  const syncToCloud = async () => {
    try {
      setSyncStatus("Salvando...");
      
      // Salvar slide atual primeiro
      saveCurrentSlideContent();

      // Aguardar um pouco para garantir que o estado foi atualizado
      await new Promise(resolve => setTimeout(resolve, 200));

      const url = dbPresentationId
        ? `${config.serverUrl}/api/presentations/${dbPresentationId}`
        : `${config.serverUrl}/api/presentations/save`;

      const response = await fetch(url, {
        method: dbPresentationId ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userId: "default-user",
          name: presentationName,
          presentation: {
            slides: slides,
            currentSlide: currentSlideIndexRef.current
          },
        }),
      });

      const data = await response.json();
      
      if (data.success) {
        if (data.presentationId) {
          setDbPresentationId(data.presentationId);
        }
        setSyncStatus("Salvo!");
        setTimeout(() => setSyncStatus(""), 3000);
        
        // Resetar countdown
        setAutoSaveCountdown(10);
      }
    } catch (error) {
      console.error("❌ Erro ao salvar:", error);
      setSyncStatus("Erro ao salvar!");
    }
  };

  // Adicionar novo slide
  const addSlide = () => {
    // Salvar slide atual primeiro
    saveCurrentSlideContent();

    // Aguardar um pouco
    setTimeout(() => {
      // Criar novo slide
      const newSlide = {
        id: uuidv4(),
        content: JSON.stringify({ objects: [], version: "5.3.0" }),
        thumbnail: ''
      };

      const newIndex = slides.length;
      
      // Adicionar o novo slide
      setSlides(prevSlides => [...prevSlides, newSlide]);
      
      // Mudar para o novo slide
      setCurrentSlideIndex(newIndex);
      currentSlideIndexRef.current = newIndex;
      
      // Limpar canvas para o novo slide
      isLoadingSlide.current = true;
      fabricRef.current.clear();
      fabricRef.current.backgroundColor = "#ffffff";
      fabricRef.current.renderAll();
      isLoadingSlide.current = false;

      console.log("➕ Novo slide adicionado no índice", newIndex);
    }, 100);
  };

  // Mudar slide - CORRIGIDA
  const changeSlide = async (index) => {
    if (index === currentSlideIndexRef.current || index < 0 || index >= slides.length) return;

    console.log(`🔄 Mudando do slide ${currentSlideIndexRef.current} para ${index}`);

    // Salvar o slide atual antes de mudar
    saveCurrentSlideContent();

    // Aguardar para garantir que foi salvo
    await new Promise(resolve => setTimeout(resolve, 200));

    // Marcar que estamos carregando um slide
    isLoadingSlide.current = true;

    // Limpar canvas
    fabricRef.current.clear();
    fabricRef.current.backgroundColor = "#ffffff";

    // Atualizar o índice
    setCurrentSlideIndex(index);
    currentSlideIndexRef.current = index;

    // Carregar o novo slide
    const targetSlide = slides[index];
    if (targetSlide && targetSlide.content) {
      try {
        const slideData = JSON.parse(targetSlide.content);
        console.log(`📄 Carregando slide ${index} com ${slideData.objects?.length || 0} objetos`);
        
        fabricRef.current.loadFromJSON(slideData, () => {
          fabricRef.current.renderAll();
          isLoadingSlide.current = false;
        });
      } catch (error) {
        console.error("Erro ao carregar slide:", error);
        fabricRef.current.renderAll();
        isLoadingSlide.current = false;
      }
    } else {
      fabricRef.current.renderAll();
      isLoadingSlide.current = false;
    }

    // Se estiver em streaming, notificar mudança
    if (isStreaming && streamId) {
      if (socketRef.current?.connected && !usePolling) {
        socketRef.current.emit("change-slide", {
          presentationId: streamId,
          slideIndex: index
        });
      } else if (usePolling) {
        // Notificar via polling
        fetch(`${config.serverUrl}/api/presentation/update`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            presentationId: "pde",
            action: "change-slide",
            currentSlide: index
          })
        });
      }
    }
  };

  // Deletar slide
  const deleteSlide = (index, e) => {
    e.stopPropagation();
    
    if (slides.length <= 1) {
      alert("Você precisa ter pelo menos um slide!");
      return;
    }

    // Se estamos deletando o slide atual, salvar primeiro
    if (index === currentSlideIndexRef.current) {
      saveCurrentSlideContent();
    }

    const newSlides = slides.filter((_, i) => i !== index);
    setSlides(newSlides);

    // Ajustar índice se necessário
    if (currentSlideIndexRef.current >= newSlides.length) {
      const newIndex = newSlides.length - 1;
      setCurrentSlideIndex(newIndex);
      currentSlideIndexRef.current = newIndex;
      changeSlide(newIndex);
    } else if (currentSlideIndexRef.current > index) {
      const newIndex = currentSlideIndexRef.current - 1;
      setCurrentSlideIndex(newIndex);
      currentSlideIndexRef.current = newIndex;
    }
  };

  // Adicionar texto
  const addText = () => {
    const text = new fabric.IText("Clique para editar", {
      left: 100,
      top: 100,
      fontFamily: "Arial",
      fontSize: 40,
      fill: "#000000",
    });

    fabricRef.current.add(text);
    fabricRef.current.setActiveObject(text);
    fabricRef.current.renderAll();

    setTimeout(() => {
      text.enterEditing();
      text.selectAll();
    }, 100);
  };

  // Adicionar retângulo
  const addRectangle = () => {
    const rect = new fabric.Rect({
      left: 100,
      top: 100,
      width: 200,
      height: 100,
      fill: "#3498db",
    });

    fabricRef.current.add(rect);
    fabricRef.current.setActiveObject(rect);
    fabricRef.current.renderAll();
  };

  // Adicionar círculo
  const addCircle = () => {
    const circle = new fabric.Circle({
      left: 100,
      top: 100,
      radius: 50,
      fill: "#e74c3c",
    });

    fabricRef.current.add(circle);
    fabricRef.current.setActiveObject(circle);
    fabricRef.current.renderAll();
  };

  // Upload de imagem
  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      fabric.Image.fromURL(event.target.result, (img) => {
        img.scaleToWidth(300);
        img.set({
          left: 100,
          top: 100,
        });
        fabricRef.current.add(img);
        fabricRef.current.setActiveObject(img);
        fabricRef.current.renderAll();
      });
    };
    reader.readAsDataURL(file);
  };

  // Aplicar estilos de texto
  const applyTextStyle = (style) => {
    const activeObject = fabricRef.current.getActiveObject();
    if (!activeObject || activeObject.type !== "i-text") return;

    switch (style) {
      case "bold":
        activeObject.set("fontWeight", activeObject.fontWeight === "bold" ? "normal" : "bold");
        setTextStyles(prev => ({ ...prev, bold: !prev.bold }));
        break;
      case "italic":
        activeObject.set("fontStyle", activeObject.fontStyle === "italic" ? "normal" : "italic");
        setTextStyles(prev => ({ ...prev, italic: !prev.italic }));
        break;
      case "underline":
        activeObject.set("underline", !activeObject.underline);
        setTextStyles(prev => ({ ...prev, underline: !prev.underline }));
        break;
      case "align-left":
        activeObject.set("textAlign", "left");
        setTextStyles(prev => ({ ...prev, align: "left" }));
        break;
      case "align-center":
        activeObject.set("textAlign", "center");
        setTextStyles(prev => ({ ...prev, align: "center" }));
        break;
      case "align-right":
        activeObject.set("textAlign", "right");
        setTextStyles(prev => ({ ...prev, align: "right" }));
        break;
    }

    fabricRef.current.renderAll();
  };

  // Mudar cor
  const changeColor = (color) => {
    const activeObject = fabricRef.current.getActiveObject();
    if (!activeObject) return;

    activeObject.set("fill", color);
    fabricRef.current.renderAll();
  };

  // Deletar objeto
  const deleteObject = () => {
    const activeObject = fabricRef.current.getActiveObject();
    if (activeObject) {
      fabricRef.current.remove(activeObject);
      fabricRef.current.renderAll();
    }
  };

  // Duplicar objeto
  const duplicateObject = () => {
    const activeObject = fabricRef.current.getActiveObject();
    if (!activeObject) return;

    activeObject.clone((cloned) => {
      cloned.set({
        left: cloned.left + 20,
        top: cloned.top + 20,
      });
      fabricRef.current.add(cloned);
      fabricRef.current.setActiveObject(cloned);
      fabricRef.current.renderAll();
    });
  };

  // Iniciar streaming - CORRIGIDA
  const startStreaming = async () => {
    console.log("🎬 Iniciando streaming...");
    
    // Limpar erros anteriores
    setStreamError("");
    
    // Salvar antes de iniciar
    saveCurrentSlideContent();
    
    // Aguardar para garantir que foi salvo
    await new Promise(resolve => setTimeout(resolve, 300));
    
    setIsGeneratingStreamId(true);

    // Obter slides atualizados
    setSlides(currentSlides => {
      const slidesToSend = currentSlides.map(slide => ({
        id: slide.id || uuidv4(),
        content: slide.content || JSON.stringify({ objects: [], version: "5.3.0" }),
        thumbnail: slide.thumbnail || ''
      }));

      console.log("📤 Enviando apresentação:", {
        slides: slidesToSend.length,
        currentSlide: currentSlideIndexRef.current
      });

      // Tentar Socket.io primeiro
      if (socketRef.current?.connected) {
        console.log("📡 Usando Socket.io...");
        socketRef.current.emit("start-presentation", {
          customId: "pde",
          presentation: {
            slides: slidesToSend,
            currentSlide: currentSlideIndexRef.current,
          },
        });
        
        // Timeout para fallback
        setTimeout(() => {
          if (!isStreaming && isGeneratingStreamId) {
            console.warn("⚠️ Socket.io timeout, tentando polling...");
            startPollingMode();
          }
        }, 3000);
      } else {
        // Fallback direto para polling
        console.warn("⚠️ Socket.io não conectado, usando polling...");
        startPollingMode();
      }

      return currentSlides;
    });
  };

  // Parar streaming
  const stopStreaming = async () => {
    console.log("🛑 Parando streaming...");
    
    if (socketRef.current?.connected && !usePolling) {
      socketRef.current.emit("stop-presentation", streamId);
    } else if (usePolling) {
      // Parar via polling
      await fetch(`${config.serverUrl}/api/presentation/update`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          presentationId: "pde",
          action: "stop"
        })
      });
    }

    setIsStreaming(false);
    setStreamId("");
    setIsGeneratingStreamId(false);
    setUsePolling(false);
    setStreamError("");
    
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }
  };

  // Copiar link
  const copyStreamLink = async () => {
    const url = `${config.clientUrl}/pde`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      alert(`Link: ${url}`);
    }
  };

  // Salvar e fechar
  const saveAndClose = async () => {
    saveCurrentSlideContent();
    await new Promise(resolve => setTimeout(resolve, 200));
    await syncToCloud();
    navigate("/");
  };

  // Navegar slides na apresentação
  const navigateSlide = (direction) => {
    const newIndex = currentSlideIndexRef.current + direction;
    if (newIndex >= 0 && newIndex < slides.length) {
      changeSlide(newIndex);
    }
  };

  return (
    <div className="editor-container">
      {/* Header */}
      <div className="editor-header">
        <div className="header-left">
          <h1 className="logo">Link2.do</h1>
          <input
            type="text"
            value={presentationName}
            onChange={(e) => setPresentationName(e.target.value)}
            className="presentation-name-input"
            placeholder="Nome da apresentação"
          />
        </div>

        <div className="header-actions">
          <button onClick={saveAndClose} className="btn btn-secondary">
            <Save size={20} />
            Salvar e Fechar
          </button>

          <button onClick={syncToCloud} className="btn btn-secondary">
            <Cloud size={20} />
            Salvar
          </button>
          
          {dbPresentationId && (
            <span className="auto-save-counter">
              auto: {autoSaveCountdown}s
            </span>
          )}
          
          {syncStatus && <span className="status-message">{syncStatus}</span>}

          {!isStreaming ? (
            <button onClick={startStreaming} className="btn btn-primary">
              <Play size={20} />
              Iniciar Apresentação
            </button>
          ) : (
            <div className="streaming-controls">
              <div className="stream-link-container">
                {isGeneratingStreamId ? (
                  <div className="stream-loading">
                    <span>Preparando apresentação...</span>
                  </div>
                ) : (
                  <div className="stream-active">
                    <Link size={20} className="stream-icon" />
                    <div className="stream-info">
                      <span className="stream-label">
                        Ao vivo em {usePolling ? '(HTTP)' : ''}:
                      </span>
                      <a
                        href={`${config.clientUrl}/pde`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="stream-url"
                      >
                        {config.clientUrl.replace(/https?:\/\//, '')}/pde
                        <ExternalLink size={14} />
                      </a>
                    </div>
                    <button
                      onClick={copyStreamLink}
                      className={`btn-stream-action ${copied ? 'copied' : ''}`}
                    >
                      {copied ? <Check size={18} /> : <Copy size={18} />}
                      {copied ? 'Copiado!' : 'Copiar'}
                    </button>
                  </div>
                )}
              </div>
              
              <button onClick={stopStreaming} className="btn btn-danger">
                <Square size={20} />
                Parar
              </button>
            </div>
          )}
          
          {streamError && (
            <span className="status-message" style={{ color: "#e74c3c" }}>
              {streamError}
            </span>
          )}
        </div>
      </div>

      <div className="editor-body">
        {/* Toolbar */}
        <div className="toolbar">
          <div className="toolbar-section">
            <button onClick={addText} className="btn-tool" title="Texto">
              <Type size={20} />
            </button>
            <button onClick={addRectangle} className="btn-tool" title="Retângulo">
              <Rectangle size={20} />
            </button>
            <button onClick={addCircle} className="btn-tool" title="Círculo">
              <CircleIcon size={20} />
            </button>
            <button onClick={() => fileInputRef.current?.click()} className="btn-tool" title="Imagem">
              <Image size={20} />
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              style={{ display: "none" }}
            />
          </div>

          {selectedObject && (
            <>
              <div className="toolbar-divider" />

              {selectedObject.type === "i-text" && (
                <div className="toolbar-section">
                  <button
                    onClick={() => applyTextStyle("bold")}
                    className={`btn-tool ${textStyles.bold ? "active" : ""}`}
                  >
                    <Bold size={20} />
                  </button>
                  <button
                    onClick={() => applyTextStyle("italic")}
                    className={`btn-tool ${textStyles.italic ? "active" : ""}`}
                  >
                    <Italic size={20} />
                  </button>
                  <button
                    onClick={() => applyTextStyle("underline")}
                    className={`btn-tool ${textStyles.underline ? "active" : ""}`}
                  >
                    <Underline size={20} />
                  </button>
                  <div className="toolbar-divider" />
                  <button
                    onClick={() => applyTextStyle("align-left")}
                    className={`btn-tool ${textStyles.align === "left" ? "active" : ""}`}
                  >
                    <AlignLeft size={20} />
                  </button>
                  <button
                    onClick={() => applyTextStyle("align-center")}
                    className={`btn-tool ${textStyles.align === "center" ? "active" : ""}`}
                  >
                    <AlignCenter size={20} />
                  </button>
                  <button
                    onClick={() => applyTextStyle("align-right")}
                    className={`btn-tool ${textStyles.align === "right" ? "active" : ""}`}
                  >
                    <AlignRight size={20} />
                  </button>
                </div>
              )}

              <div className="toolbar-section">
                <input
                  type="color"
                  onChange={(e) => changeColor(e.target.value)}
                  className="color-picker"
                  title="Cor"
                />
                <button onClick={duplicateObject} className="btn-tool" title="Duplicar">
                  <Copy size={20} />
                </button>
                <button onClick={deleteObject} className="btn-tool btn-danger" title="Deletar">
                  <Trash2 size={20} />
                </button>
              </div>
            </>
          )}
        </div>

        <div className="editor-main">
          {/* Sidebar de slides */}
          <div className="slides-sidebar">
            <div className="slides-header">
              <h3>Slides</h3>
              <button onClick={addSlide} className="btn-icon">
                <Plus size={20} />
              </button>
            </div>

            <div className="slides-list">
              {slides.map((slide, index) => (
                <div
                  key={slide.id}
                  className={`slide-item ${index === currentSlideIndex ? "active" : ""}`}
                  onClick={() => changeSlide(index)}
                >
                  <div className="slide-number">{index + 1}</div>
                  <div className="slide-thumbnail">
                    {slide.thumbnail ? (
                      <img src={slide.thumbnail} alt={`Slide ${index + 1}`} />
                    ) : (
                      <div className="empty-thumbnail">
                        <Rectangle size={40} />
                      </div>
                    )}
                  </div>
                  <button
                    onClick={(e) => deleteSlide(index, e)}
                    className="slide-delete"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Canvas */}
          <div className="canvas-container">
            <div className="canvas-wrapper">
              <canvas ref={canvasRef} />
            </div>

            {isStreaming && (
              <div className="presentation-controls">
                <button
                  onClick={() => navigateSlide(-1)}
                  disabled={currentSlideIndex === 0}
                  className="btn-nav"
                >
                  <ChevronLeft size={32} />
                </button>
                <span className="slide-counter">
                  {currentSlideIndex + 1} / {slides.length}
                </span>
                <button
                  onClick={() => navigateSlide(1)}
                  disabled={currentSlideIndex === slides.length - 1}
                  className="btn-nav"
                >
                  <ChevronRight size={32} />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Editor;